"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSidebar } from "@speakai/ui/sidebar";
import {
  primaryMenuItems,
  menuSections,
  settingsMenuGroups,
  filterSettingsGroups,
  activeBadge,
  type MenuItem,
  type MenuSection,
} from "@/config/menu";
import { getPersonaBucket, type ApolloPersona } from "@/features/onboarding/persona";
import { canonicalizeGoal } from "@/features/onboarding/goal-canonical";
import { getRecorderLabelKey } from "@/features/onboarding/bucket-labels";
import { usePersonalizationContext } from "@/hooks/use-personalization-context";
import { useMenuPermissions } from "@/hooks/use-menu-permissions";
import { usePermissions } from "@/hooks/use-permissions";
import { useIntercom } from "@/hooks/use-intercom";
import { useFolderList } from "@/features/folder/queries";
import { FolderDialog } from "@/features/folder/components/folder-dialog";
import {
  ArrowLeft,
  Plus,
  Lock,
  Folder,
  ExternalLink,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { changeLanguage } from "@/lib/integrations/i18n";
import { PLATFORM_LANGUAGES } from "@speakai/common/src/config/platform-languages";
import { cn } from "@/lib/utils/cn";
import { useAuth } from "@/hooks/use-auth";
import { trackFunnel } from "@/lib/integrations/sentry-tracking";
import { pushDataLayer } from "@/lib/integrations/ga4";
import { useUpdatePlatformLanguage } from "@/features/profile/mutations";
import { useAccountUsage } from "@/features/dashboard/queries";
import { SubscriptionPlanId } from "@/features/subscription/types";
import { getPlanName } from "@/features/subscription/lib/plan-name";
import type { TeamMemberPermission } from "@/features/team/api";
import { deriveCredits } from "@/features/subscription/hooks/use-credits";
import { clampTrialDays, trialUrgencyTier } from "@/lib/utils/trial";
import { isTrialActive as isTrialActiveFor } from "@/features/subscription/utils/subscription-status";
import { SidebarSection, trackSidebarEvent, type SidebarItem } from "@/components/layout/sidebar-primitives";
import { SidebarNavigation } from "@/components/layout/sidebar-navigation";
import { SidebarFooter } from "@/components/layout/sidebar-footer";
import {
  resolveSideMenuOrder,
  resolveSectionOrder,
  mergeSideMenuWithCompanyDefault,
  MENU_ITEM_LABEL_KEYS,
  SECTION_LABEL_KEYS,
  type SideMenuPreferences,
} from "@/components/layout/menu-preferences";

interface CohortLayout {
  primaryOrder: string[];
  sectionOrder: string[];
}

const DEFAULT_PRIMARY_ORDER = ["home", "dashboards", "explore", "meeting-assistant", "chat", "favorites"];
const DEFAULT_SECTION_ORDER = ["folders", "content", "agents", "workspace"];

const cohortLayouts: Record<ApolloPersona, CohortLayout> = {
  // ICP-1 ($9.3k/mo, 51 users) — 18,940 magic_prompt queries/mo. Chat IS the surface.
  QUALITATIVE_INSIGHT: {
    primaryOrder: ["home", "dashboards", "chat", "explore", "meeting-assistant", "favorites"],
    sectionOrder: ["folders", "workspace", "content", "agents"],
  },
  // ICP-2 ($960/mo, 26 users) — 1,289 meeting_assistance sessions. Meeting promoted.
  MEDIA_INTENSIVE: {
    primaryOrder: ["home", "dashboards", "meeting-assistant", "explore", "chat", "favorites"],
    sectionOrder: ["folders", "content", "agents", "workspace"],
  },
  // ICP-3 ($175/mo, 32 users, solo) — Meeting-first; Favorites demoted.
  PROFESSIONAL_BUYER: {
    primaryOrder: ["home", "dashboards", "meeting-assistant", "chat", "explore", "favorites"],
    sectionOrder: ["folders", "content", "agents", "workspace"],
  },
  SALES: {
    primaryOrder: ["home", "meeting-assistant", "dashboards", "explore", "chat", "favorites"],
    sectionOrder: DEFAULT_SECTION_ORDER,
  },
  DEFAULT: {
    primaryOrder: DEFAULT_PRIMARY_ORDER,
    sectionOrder: DEFAULT_SECTION_ORDER,
  },
};

/** Goal-bucket layouts outrank Apollo persona cohorts; user drag-reorder prefs outrank both. */
const goalBucketLayouts: Record<string, CohortLayout> = {
  Sales: {
    primaryOrder: ["home", "meeting-assistant", "dashboards", "explore", "chat", "favorites"],
    sectionOrder: DEFAULT_SECTION_ORDER,
  },
  Research: {
    primaryOrder: ["home", "explore", "dashboards", "chat", "meeting-assistant", "favorites"],
    sectionOrder: ["folders", "content", "workspace"],
  },
  Meetings: {
    primaryOrder: ["home", "meeting-assistant", "dashboards", "chat", "explore", "favorites"],
    sectionOrder: DEFAULT_SECTION_ORDER,
  },
};

// Pre-index static menu data at module load — avoids Map reconstruction per render.
const primaryById: Map<string, MenuItem> = new Map(primaryMenuItems.map((item) => [item.id, item]));
const sectionById: Map<string, MenuSection> = new Map(menuSections.map((section) => [section.id, section]));

function BadgePill({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
      {children}
    </span>
  );
}

interface SidebarProps {
  userName?: string;
  userEmail?: string;
  userAvatar?: string;
}

export function AppSidebar({ userName, userEmail, userAvatar }: SidebarProps) {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { collapsed, setCollapsed, setMobileOpen } = useSidebar();
  const { show: showIntercom } = useIntercom();
  const { filterItem, filterSection, isItemLocked } = useMenuPermissions();
  const { canManageBilling } = usePermissions();
  const { logout, user } = useAuth();

  const sideMenuPrefs = mergeSideMenuWithCompanyDefault(
    user?.preferences?.sideMenu as SideMenuPreferences | undefined,
    user?.companyNavDefaults?.sideMenu,
  );

  // Sticky-persona invariant: compute the bucket once per sidebar mount; late
  // Apollo enrichment or mid-session goal capture does not re-bucket.
  const personaCtx = usePersonalizationContext();
  const personaBucketRef = useRef<ApolloPersona>(getPersonaBucket(personaCtx));
  const goalLayoutRef = useRef<CohortLayout | null>(
    goalBucketLayouts[canonicalizeGoal(personaCtx.onboardingGoal)] ?? null,
  );
  const cohortLayout = goalLayoutRef.current ?? cohortLayouts[personaBucketRef.current];

  const { data: subscription } = useAccountUsage();

  const planIsSubscription =
    subscription?.planId === SubscriptionPlanId.Speak_Custom ||
    subscription?.planId === SubscriptionPlanId.Speak_Build_Custom_Plan;

  const { t } = useTranslation();

  const updatePlatformLanguage = useUpdatePlatformLanguage();
  const [changingLanguage, setChangingLanguage] = useState<string | null>(null);

  const handleLanguageChange = useCallback(async (code: string) => {
    const langLabel = PLATFORM_LANGUAGES.find((l) => l.code === code)?.label ?? code;
    setChangingLanguage(langLabel);
    try {
      await changeLanguage(code);
      await updatePlatformLanguage.mutateAsync(code);
    } catch {
      // Local change still applied even if API fails
    } finally {
      setChangingLanguage(null);
    }
  }, [updatePlatformLanguage]);

  const [folderDialogOpen, setFolderDialogOpen] = useState(false);

  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  const handleSelectLanguage = useCallback(async (code: string) => {
    await handleLanguageChange(code);
    setProfileMenuOpen(false);
  }, [handleLanguageChange]);

  // Fetch 5 folders for sidebar — surface the 3 most recently touched.
  // Client sorts by updatedAt desc: drag reorder, folder create, rename, and media
  // moves all bump updatedAt — so MRU captures both explicit drag intent AND passive
  // activity without any device-local state (critical for multi-device power users).
  // Tie-break on showOrder asc preserves manual order among same-timestamp folders.
  const { data: folderData } = useFolderList({
    page: 0,
    pageSize: 5,
    sortBy: "showOrder",
    sortDir: "asc",
  });

  const folders = useMemo(() => {
    const all = folderData?.folders ?? [];
    if (all.length === 0) return all;
    return [...all]
      .sort((a, b) => {
        const aTime = new Date(a.updatedAt ?? 0).getTime();
        const bTime = new Date(b.updatedAt ?? 0).getTime();
        if (bTime !== aTime) return bTime - aTime;
        return (a.showOrder ?? 0) - (b.showOrder ?? 0);
      })
      .slice(0, 3);
  }, [folderData]);

  // Determine if items should be disabled (free trial + no uploads)
  const isFreeTrial = useMemo(() => {
    if (!subscription) return false;
    return (
      subscription.planId === SubscriptionPlanId.Speak_Free_Trial &&
      !subscription.isExpired &&
      (subscription.totalMedia ?? 0) === 0
    );
  }, [subscription]);

  // UI-CHROME-ONLY — canManageBilling is a display gate; server RBAC enforces /pricing + billing independently.
  const isTrialActive = isTrialActiveFor(subscription);
  const trialDaysLeft = clampTrialDays(subscription?.remainingDays);
  const trialTier = trialUrgencyTier(subscription?.isExpired, subscription?.remainingDays);

  useEffect(() => {
    if (isTrialActive && canManageBilling) {
      trackFunnel("sidebar", "sidebar_trial_card_impression", { days_left: trialDaysLeft });
      pushDataLayer("Sidebar", "TrialCardImpression", undefined, undefined, { days_left: trialDaysLeft });
    }
    // trialDaysLeft intentionally excluded — fire once when the card first becomes visible;
    // re-firing on each day-count tick would over-count impressions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTrialActive, canManageBilling]);

  const handleTrialSubscribe = useCallback(() => {
    trackFunnel("sidebar", "sidebar_trial_card_click", { days_left: trialDaysLeft });
    pushDataLayer("Sidebar", "TrialCardClick", undefined, undefined, { days_left: trialDaysLeft });
  }, [trialDaysLeft]);

  const isActive = useCallback(
    (path?: string) => {
      if (!path) return false;
      if (path === "/home") return pathname === path;
      return pathname?.startsWith(path) ?? false;
    },
    [pathname]
  );

  const handleMobileClose = useCallback(() => {
    setMobileOpen(false);
  }, [setMobileOpen]);

  const handleFolderCreated = useCallback(
    (folderId?: string) => {
      if (folderId) {
        navigate(`/folder/${folderId}`);
      }
    },
    [navigate]
  );

  const handleAction = useCallback(
    (action: string) => {
      if (action === "createFolder") {
        setFolderDialogOpen(true);
      } else if (action === "openChat") {
        showIntercom();
      }
    },
    [showIntercom]
  );

  const menuItemTranslationKeys: Record<string, string> = useMemo(() => ({
    ...MENU_ITEM_LABEL_KEYS,
    "folders-root": "MENU.FOLDERS",
  }), []);

  // Build menu item → SidebarItem mapper
  const mapItem = useCallback(
    (item: MenuItem): SidebarItem => {
      const disabled = isFreeTrial && item.disableOnFreeTrial;
      const locked = isItemLocked(item);

      // Build badge: "NEW" pill + external link icon, or "Talk to us" chip for locked upsell items
      const badgeKey = activeBadge(item);
      let badge: React.ReactNode = undefined;
      if (locked) {
        // Locked upsell: inline chip — not disabled, so the click reaches Intercom
        badge = <BadgePill>{t("MENU.TALK_TO_US", "Talk to us")}</BadgePill>;
      } else if (badgeKey || item.external) {
        badge = (
          <span className="flex items-center gap-1 shrink-0">
            {badgeKey && <BadgePill>{t(badgeKey)}</BadgePill>}
            {item.external && (
              <ExternalLink className="h-3 w-3 text-muted-foreground" />
            )}
          </span>
        );
      }

      const translationKey = menuItemTranslationKeys[item.id];
      const recorderLabel = item.id === "recorder" ? getRecorderLabelKey(user?.onboarding?.goal) : undefined;
      const label = recorderLabel
        ? t(recorderLabel.key, recorderLabel.fallback)
        : translationKey
          ? t(translationKey)
          : item.title;

      // Locked upsell content is data-driven from the menu config so the same
      // hover tooltip copy serves any future locked feature. The click opens the
      // global subscription gate (PricingPage) — same workflow as every other gated feature.
      const upsell = locked
        ? {
            title: label,
            description: item.lockedDescriptionKey ? t(item.lockedDescriptionKey) : "",
          }
        : undefined;

      return {
        id: item.id,
        label,
        icon: disabled || locked ? (
          <Lock className="h-[18px] w-[18px]" />
        ) : (
          <item.icon size={18} />
        ),
        href: item.external
          ? item.externalUrl
          : disabled || locked
            ? undefined
            : item.path,
        active: isActive(item.path),
        // Locked upsell is NOT disabled — pointer-events-none would block the click
        disabled: disabled && !locked,
        badge,
        lockedUpsell: upsell,
        onClick: locked
          ? () => showIntercom()
          : item.action
            ? () => handleAction(item.action!)
            : undefined,
      };
    },
    [isActive, isFreeTrial, isItemLocked, handleAction, showIntercom, menuItemTranslationKeys, t, user]
  );

  // Build speak-ui SidebarSection[] from menu config
  const sections: SidebarSection[] = useMemo(() => {
    const orderedPrimary: MenuItem[] = resolveSideMenuOrder(
      cohortLayout.primaryOrder,
      sideMenuPrefs,
      primaryById.keys(),
    )
      .map((id) => primaryById.get(id))
      .filter((item): item is MenuItem => item !== undefined);

    const result: SidebarSection[] = [
      {
        id: "primary",
        items: orderedPrimary.filter(filterItem).map(mapItem),
      },
    ];

    // Cohort-ordered sections + fail-safe append for future sections not yet
    // enumerated in any cohortLayout (forward-compat — prevents silent drops).
    const orderedIds = new Set(cohortLayout.sectionOrder);
    const fallbackTail = menuSections.map((s) => s.id).filter((id) => !orderedIds.has(id));
    const baseSectionOrder = [...cohortLayout.sectionOrder, ...fallbackTail];
    const sectionIds = resolveSectionOrder(baseSectionOrder, sideMenuPrefs);

    for (const sectionId of sectionIds) {
      const section = sectionById.get(sectionId);
      if (!section) continue;
      const filtered = filterSection(section);
      if (!filtered) continue;

      // Special handling for folders section: inject dynamic folders
      if (section.id === "folders") {
        const folderItems: SidebarItem[] = [];

        // "Folders" header with + button
        folderItems.push({
          id: "folders-root",
          label: t("MENU.FOLDERS"),
          icon: <Folder size={18} />,
          href: "/folder",
          active: pathname === "/folder",
          badge: !collapsed ? (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setFolderDialogOpen(true);
              }}
              className="flex items-center justify-center w-5 h-5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              aria-label={t("SIDEBAR.CREATE_FOLDER")}
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          ) : undefined,
        });

        // Dynamic folder list
        for (const folder of folders) {
          folderItems.push({
            id: `folder-${folder.folderId}`,
            label: folder.name,
            icon: <Folder className="h-[18px] w-[18px]" />,
            href: `/folder/${folder.folderId}`,
            active: pathname === `/folder/${folder.folderId}`,
          });
        }

        result.push({
          id: "folders",
          label: collapsed ? t("MENU.FOLDERS") : undefined,
          items: folderItems,
        });
        continue;
      }

      const sectionItemIds = filtered.items.map((item) => item.id);
      const itemById = new Map(filtered.items.map((item) => [item.id, item]));
      const orderedItems = resolveSideMenuOrder(sectionItemIds, sideMenuPrefs, sectionItemIds)
        .map((id) => itemById.get(id))
        .filter((item): item is MenuItem => item !== undefined);

      const sectionItems = orderedItems.map((item) => mapItem(item));
      if (sectionItems.length === 0) continue;

      const translatedLabel = filtered.label
        ? (SECTION_LABEL_KEYS[filtered.id] ? t(SECTION_LABEL_KEYS[filtered.id]) : filtered.label)
        : undefined;

      result.push({
        id: filtered.id,
        label: translatedLabel,
        items: sectionItems,
      });
    }

    return result;
    // cohortLayout is derived from a ref and never changes mid-session — sticky invariant.
    // Including it in deps is ESLint-required; it will never trigger re-computation.
  }, [
    filterItem,
    filterSection,
    mapItem,
    folders,
    collapsed,
    pathname,
    t,
    cohortLayout,
    sideMenuPrefs,
  ]);

  const isSettingsRoute = pathname === "/profile" || pathname.startsWith("/profile/");
  const lastAppPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isSettingsRoute) lastAppPathRef.current = `${pathname}${search}`;
  }, [isSettingsRoute, pathname, search]);

  const settingsSections: SidebarSection[] = useMemo(() => {
    if (!isSettingsRoute) return [];
    const permission = user?.permission as TeamMemberPermission | undefined;
    const groups = filterSettingsGroups(settingsMenuGroups, permission?.profileSettings);
    return [
      {
        id: "settings-back",
        items: [
          {
            id: "settings-back",
            label: t("PROFILE.SETTINGS_LABEL", "Settings"),
            ariaLabel: t("SIDEBAR.MENU.BACK_TO_APP", "Back to App"),
            icon: <ArrowLeft className="h-[18px] w-[18px]" />,
            onClick: () => {
              trackSidebarEvent("Sidebar", "Settings Back");
              navigate(lastAppPathRef.current ?? "/home");
            },
          },
        ],
      },
      ...groups.map((group, index) => ({
        id: `settings-${group.labelKey || index}`,
        label: group.labelKey ? t(group.labelKey) : undefined,
        items: group.items.map((item): SidebarItem => {
          const Icon = item.icon;
          const badgeKey = activeBadge(item);
          return {
            id: item.href,
            label: t(item.labelKey, item.labelFallback),
            icon: <Icon className="h-[18px] w-[18px]" />,
            href: item.href,
            active: pathname === item.href,
            badge: badgeKey ? <BadgePill>{t(badgeKey)}</BadgePill> : undefined,
          };
        }),
      })),
    ];
  }, [isSettingsRoute, user, t, navigate, pathname]);

  const displayName = userName ?? "User";

  const toggleCollapsed = useCallback(() => {
    setCollapsed(!collapsed);
  }, [collapsed, setCollapsed]);

  // ⌘\ (Mac) / Ctrl+\ (Windows/Linux) toggles the sidebar collapse state.
  // Uses e.code ("Backslash") rather than e.key so it stays layout-independent,
  // matching the convention in use-quick-action-shortcuts. Bare modifier + key
  // (no Shift) so it reads as the canonical "toggle sidebar" chord.
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey) return;
      if (e.code !== "Backslash") return;
      const el = document.activeElement as HTMLElement | null;
      const tag = el?.tagName.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (el?.isContentEditable) return;
      e.preventDefault();
      toggleCollapsed();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleCollapsed]);

  const usedMinutes = subscription?.usedMinutes ?? 0;
  const totalMinutes = subscription?.freeMinutes ?? 1;
  const { creditMode, remaining: creditsRemaining } = deriveCredits(subscription);
  const usagePercent = Math.min(100, (usedMinutes / totalMinutes) * 100);
  const isApproachingLimit = usagePercent >= 80;

  const billingPeriodLabel =
    subscription?.durationId === "yearly" ? t("SIDEBAR.THIS_YEAR") :
      subscription?.durationId === "monthly" ? t("SIDEBAR.THIS_MONTH") :
        null;

  const billEndDate = subscription?.billEndDate
    ? new Date(subscription.billEndDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : "";


  return (
    <div className="relative flex flex-col h-full bg-card border-r border-border">
      {/* Collapsed: click on border area to expand */}
      {collapsed && (
        <button
          onClick={toggleCollapsed}
          className="absolute right-0 top-0 h-full w-2 cursor-pointer hover:bg-primary/10 transition-colors z-10 hidden md:block"
          aria-label={t("SIDEBAR.OPEN_SIDEBAR")}
        />
      )}


      <SidebarNavigation
        collapsed={collapsed}
        sections={isSettingsRoute ? settingsSections : sections}
        onNavigate={handleMobileClose}
      />

      {/* Footer */}
      <div className={cn("shrink-0 border-t border-border", collapsed ? "p-2" : "px-3 py-3")}>
        <SidebarFooter
          billingPeriodLabel={billingPeriodLabel}
          billEndDate={billEndDate}
          collapsed={collapsed}
          changingLanguage={changingLanguage}
          onSelectLanguage={handleSelectLanguage}
          onToggleCollapsed={toggleCollapsed}
          planName={getPlanName(subscription?.planId)}
          displayName={displayName}
          canManageBilling={canManageBilling}
          creditMode={creditMode}
          creditsRemaining={creditsRemaining}
          isApproachingLimit={isApproachingLimit}
          isTrialActive={isTrialActive}
          trialDaysLeft={trialDaysLeft}
          trialTier={trialTier}
          onTrialSubscribe={handleTrialSubscribe}
          logout={logout}
          planIsSubscription={planIsSubscription}
          profileMenuOpen={profileMenuOpen}
          routerPush={(href) => navigate(href)}
          setProfileMenuOpen={setProfileMenuOpen}
          t={t}
          totalMinutes={totalMinutes}
          usagePercent={usagePercent}
          usedMinutes={usedMinutes}
          userAvatar={userAvatar}
          userEmail={userEmail}
        />
      </div>

      <FolderDialog
        open={folderDialogOpen}
        onClose={() => setFolderDialogOpen(false)}
        onSuccess={handleFolderCreated}
      />
    </div>
  );
}
